<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { createI18n } from '@drama/i18n'
import {
  PREVIEW_KINDS,
  PREVIEW_LOCALES,
  assertSimulatedOnly,
  missingEmailLocales,
  previewCards,
  renderPreviewEmail,
  requestPreview,
} from '../../lib/story-film-notification-preview.js'

/**
 * G：Story 成片通知的**本地模拟预览**（管理后台评审用，独立组件）。
 *
 * 边界（03 场景 I）：
 * - 纯前端固定载荷，不读库、不写库、不发邮件、不扣费、不触供应商；本组件不注入 `api`。
 * - 每张卡片带醒目的「模拟」徽标；成功卡片也没有可播地址，点播放只解释「模拟不提供播放」，不是死链。
 * - 失败卡片只做视觉评审，不出现退款金额、不触发退款。
 * - 同一 kind 重复请求命中同一 id：只提示「不会重复通知」，不新增第二条。
 * - 文案全部来自 `storyFilm.preview.*` 与既有 `storyFilm.notification.*`（六语由 E 落盘）。
 *
 * 本组件不自行注册路由：由后台制作总装配（B）在 prompt review 流程中登记并挂入口。
 */
const { t } = useI18n()

const cards = previewCards()
assertSimulatedOnly(cards)

const hits = ref(new Map())
const playNoticeKey = ref('')
const lastRequest = ref(null)
/** 六语对照行：页面文案来自 @drama/i18n，邮件正文来自真实模板；任一渲染失败都显式标出。 */
const localeRows = ref([])
const localeRowsState = ref('loading')
const emailMissing = ref(missingEmailLocales())

onMounted(() => {
  void buildLocaleRows()
})

function localeText(runtime, key) {
  return runtime.i18n.global.t(key)
}

async function buildLocaleRows() {
  localeRowsState.value = 'loading'
  const rows = []
  try {
    const runtime = createI18n({ locale: 'en' })
    await runtime.ready
    for (const locale of PREVIEW_LOCALES) {
      await runtime.setLocale(locale.code)
      const cards = PREVIEW_KINDS.map(kind => {
        const card = previewCards().find(item => item.key === kind)
        return {
          key: kind,
          label: card.failed_preview === true
            ? localeText(runtime, 'storyFilm.preview.failedLabel')
            : localeText(runtime, card.is_free ? 'storyFilm.notification.readyFree' : 'storyFilm.notification.ready'),
          title: localeText(runtime, card.title_key),
          summary: localeText(runtime, card.summary_key),
        }
      })
      const email = renderPreviewEmail({
        email_locale: locale.email_locale,
        episode_title: localeText(runtime, 'storyFilm.preview.sampleEpisodeTitle'),
        episode_summary: localeText(runtime, 'storyFilm.preview.sampleEpisodeSummary'),
      })
      rows.push({ code: locale.code, label: locale.label, cards, email })
    }
    localeRows.value = rows
    localeRowsState.value = 'ready'
  } catch {
    localeRows.value = []
    localeRowsState.value = 'failed'
  }
}

const cardsWithLabels = computed(() => cards.map(card => ({
  ...card,
  labelKey: labelKeyOf(card),
})))

function labelKeyOf(card) {
  if (card.failed_preview === true) return 'storyFilm.preview.failedLabel'
  return card.is_free ? 'storyFilm.notification.readyFree' : 'storyFilm.notification.ready'
}

function explainUnavailable(card) {
  playNoticeKey.value = card.key
}

function repeat(card) {
  lastRequest.value = requestPreview(card.key, hits.value)
}

function statusText(request) {
  if (request === null) return ''
  return request.duplicate ? t('storyFilm.preview.duplicate') : ''
}
</script>

<template>
  <section class="story-film-preview" data-testid="story-film-preview">
    <header class="story-film-preview-header">
      <span class="story-film-preview-badge" data-testid="story-film-preview-badge">{{ t('storyFilm.preview.badge') }}</span>
      <h2>{{ t('storyFilm.preview.bannerTitle') }}</h2>
      <p>{{ t('storyFilm.preview.bannerBody') }}</p>
    </header>

    <ul class="story-film-preview-cards">
      <li
        v-for="card in cardsWithLabels"
        :key="card.id"
        class="story-film-preview-card"
        :class="{ 'is-failed': card.failed_preview === true }"
        :data-testid="`story-film-preview-card-${card.key}`"
      >
        <span class="story-film-preview-card-badge">{{ t('storyFilm.preview.badge') }}</span>
        <strong :data-testid="`story-film-preview-label-${card.key}`">{{ t(card.labelKey) }}</strong>
        <p class="story-film-preview-title">{{ t(card.title_key) }}</p>
        <p class="story-film-preview-summary">{{ t(card.summary_key) }}</p>
        <p class="story-film-preview-id" :data-testid="`story-film-preview-id-${card.key}`">{{ card.id }}</p>
        <div class="story-film-preview-actions">
          <button
            v-if="card.failed_preview !== true"
            type="button"
            :data-testid="`story-film-preview-play-${card.key}`"
            @click="explainUnavailable(card)"
          >{{ t('storyFilm.notification.watchEpisode') }}</button>
          <p
            v-else
            class="story-film-preview-failed-action"
            :data-testid="`story-film-preview-failed-action-${card.key}`"
          >{{ t('storyFilm.player.mediaUnavailable') }}</p>
          <button
            type="button"
            :data-testid="`story-film-preview-repeat-${card.key}`"
            @click="repeat(card)"
          >{{ card.key }}</button>
        </div>
        <p
          v-if="playNoticeKey === card.key"
          class="story-film-preview-note"
          role="status"
          :data-testid="`story-film-preview-play-note-${card.key}`"
        >{{ t('storyFilm.preview.playUnavailable') }}</p>
        <p
          v-if="lastRequest !== null && lastRequest.kind === card.key && lastRequest.duplicate"
          class="story-film-preview-note"
          role="status"
          :data-testid="`story-film-preview-repeat-note-${card.key}`"
        >{{ statusText(lastRequest) }}</p>
      </li>
    </ul>

    <p class="story-film-preview-kinds" data-testid="story-film-preview-kinds">{{ PREVIEW_KINDS.join(' · ') }}</p>

    <section class="story-film-preview-locales" data-testid="story-film-preview-locales">
      <h3>{{ t('storyFilm.preview.bannerTitle') }} · {{ PREVIEW_LOCALES.map(locale => locale.label).join(' / ') }}</h3>
      <p
        v-if="emailMissing.length > 0"
        class="story-film-preview-note"
        data-testid="story-film-preview-email-missing"
      >{{ emailMissing.join(', ') }}</p>
      <p
        v-if="localeRowsState === 'failed'"
        class="story-film-preview-note"
        data-testid="story-film-preview-locales-failed"
      >{{ t('storyFilm.preview.playUnavailable') }}</p>
      <ol v-else class="story-film-preview-locale-list">
        <li
          v-for="row in localeRows"
          :key="row.code"
          class="story-film-preview-locale"
          :data-testid="`story-film-preview-locale-${row.code}`"
        >
          <h4>{{ row.label }} · {{ row.code }}</h4>
          <ul class="story-film-preview-locale-cards">
            <li v-for="card in row.cards" :key="card.key">
              <span class="story-film-preview-locale-kind">{{ card.key }}</span>
              <strong>{{ card.label }}</strong>
              <p class="story-film-preview-title">{{ card.title }}</p>
              <p class="story-film-preview-summary">{{ card.summary }}</p>
            </li>
          </ul>
          <div class="story-film-preview-email" :data-testid="`story-film-preview-email-${row.code}`">
            <span class="story-film-preview-locale-kind">email</span>
            <p class="story-film-preview-title">{{ row.email.subject }}</p>
            <pre class="story-film-preview-email-text">{{ row.email.text }}</pre>
          </div>
        </li>
      </ol>
    </section>
  </section>
</template>

<style scoped>
.story-film-preview { display: grid; gap: var(--drama-space-lg); }
.story-film-preview-header { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-md); border: 1px dashed var(--drama-color-warning); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-raised); }
.story-film-preview-header h2 { margin: 0; font-size: var(--drama-font-size-lg); }
.story-film-preview-header p { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.story-film-preview-badge, .story-film-preview-card-badge { display: inline-block; justify-self: start; padding: 2px 8px; border-radius: var(--drama-radius-full); background: var(--drama-color-warning); color: var(--drama-color-on-primary); font-size: var(--drama-font-size-xs); font-weight: var(--drama-font-weight-medium); }
.story-film-preview-cards { display: grid; gap: var(--drama-space-md); margin: 0; padding: 0; list-style: none; }
.story-film-preview-card { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-md); border: 1px dashed var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); }
.story-film-preview-card.is-failed { border-color: var(--drama-color-danger); }
.story-film-preview-card strong { font-size: var(--drama-font-size-sm); }
.story-film-preview-title { margin: 0; font-size: var(--drama-font-size-sm); }
.story-film-preview-summary, .story-film-preview-id, .story-film-preview-kinds { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.story-film-preview-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.story-film-preview-actions button { padding: 6px 12px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-raised); color: var(--drama-color-text); font: inherit; font-size: var(--drama-font-size-xs); cursor: pointer; }
.story-film-preview-note { margin: 0; color: var(--drama-color-warning); font-size: var(--drama-font-size-xs); }
.story-film-preview-failed-action { margin: 0; color: var(--drama-color-danger); font-size: var(--drama-font-size-xs); }
.story-film-preview-locales { display: grid; gap: var(--drama-space-sm); }
.story-film-preview-locales h3 { margin: 0; font-size: var(--drama-font-size-md); }
.story-film-preview-locale-list { display: grid; gap: var(--drama-space-md); margin: 0; padding: 0; list-style: none; }
.story-film-preview-locale { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); }
.story-film-preview-locale h4 { margin: 0; font-size: var(--drama-font-size-sm); }
.story-film-preview-locale-cards { display: grid; gap: var(--drama-space-xs); margin: 0; padding: 0; list-style: none; }
.story-film-preview-locale-cards li { display: grid; gap: 2px; }
.story-film-preview-locale-kind { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-transform: uppercase; letter-spacing: .04em; }
.story-film-preview-email { display: grid; gap: var(--drama-space-xs); padding-top: var(--drama-space-xs); border-top: 1px dashed var(--drama-color-border); }
.story-film-preview-email-text { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font-family: inherit; font-size: var(--drama-font-size-xs); color: var(--drama-color-text-muted); }
</style>
